<template>
  <section class="card">
    <button
      class="back-btn"
      type="button"
      title="返回首页入口管理"
      @click="goBackToTiles"
      @mouseenter="backBtnHover = true"
      @mouseleave="backBtnHover = false"
    >
      <img class="back-btn-icon" :src="backBtnHover ? backAboutActiveIcon : backAboutIcon" alt="返回" />
    </button>

    <div class="page-head">
      <h2>表彰公示管理</h2>
      <button class="btn btn-primary" :disabled="saving" @click="saveAll">
        {{ saving ? '保存中...' : '保存' }}
      </button>
    </div>

    <p class="tip">
      说明：本页用于维护小程序「表彰公示」内容，分为 <strong>文档</strong>（PDF）与 <strong>文章</strong>（公众号推文）两栏。
      保存写入 <code>cms_page_content</code> 的 <code>content_key=award</code>（兼容旧 key：<code>award_list</code>）。
    </p>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>
    <p v-if="okMsg" class="ok">{{ okMsg }}</p>

    <div class="panel tile-panel">
      <h3>首页入口（表彰公示）</h3>
      <div class="tile-grid">
        <label class="field inline">
          <span>入口标题</span>
          <input v-model="tileForm.title" placeholder="例如：表彰公示" />
        </label>
        <label class="field inline">
          <span>排序值（只读）</span>
          <input :value="tileForm.sortOrder || 0" disabled />
        </label>
        <div class="field inline tile-upload">
          <span>入口图片</span>
          <div class="img-row">
            <div class="upload-box upload-box--sm" @click="pickTileImage" @contextmenu.prevent="pickTileImage">
              <img v-if="tileForm.imageUrl" class="upload-img" :src="toFullUrl(tileForm.imageUrl)" alt="" />
              <div v-else class="upload-placeholder">
                <div class="upload-plus">+</div>
                <div class="upload-text">点击上传入口图</div>
              </div>
            </div>
            <div class="upload-hint">提示：点击图片可更改。</div>
          </div>
          <input ref="tileImageInput" class="hidden-input" type="file" accept="image/*" @change="onTileImageChange" />
        </div>
        <label class="field inline">
          <span>入口状态</span>
          <FancySelect v-model="tileForm.statusCode" :options="statusOptions" icon="●" />
        </label>
        <label class="field inline">
          <span>页面类型</span>
          <FancySelect
            v-model="tileForm.pageTypeId"
            :options="pageTypeOptions"
            icon="📄"
            placeholder="不标注类型"
            :searchable="true"
          />
        </label>
      </div>
    </div>

    <nav class="tabs" aria-label="表彰公示导航">
      <button type="button" class="tab" :class="{ active: activeTab === 'doc' }" @click="activeTab = 'doc'">文档</button>
      <button type="button" class="tab" :class="{ active: activeTab === 'article' }" @click="activeTab = 'article'">文章</button>
    </nav>

    <div class="panel">
      <div class="panel-head">
        <h3>{{ activeTab === 'doc' ? '文档条目（PDF）' : '文章条目（公众号推文）' }}</h3>
        <button class="btn btn-edit" @click="openItemAdd">新增条目</button>
      </div>
      <p class="subtip">提示：按住每行左侧“⋮⋮”可拖拽排序；点击“编辑”修改条目。</p>

      <div v-if="!currentItems.length" class="empty">暂无条目，请点击右上角「新增条目」。</div>
      <div
        v-for="(it, idx) in currentItems"
        :key="it._k"
        class="item-row"
        draggable="true"
        @dragstart="onDragStart(idx)"
        @dragover.prevent
        @drop="onDrop(idx)"
        @dragend="onDragEnd"
      >
        <span class="drag-handle" title="拖拽排序">⋮⋮</span>
        <span class="item-idx">{{ idx + 1 }}</span>
        <div class="item-main">
          <div class="item-title">{{ it.title || '（未填写标题）' }}</div>
          <div class="item-meta">
            <span v-if="it.date">{{ it.date }}</span>
            <span v-if="activeTab === 'doc' && it.fileUrl">已上传PDF</span>
            <span v-if="activeTab === 'article' && it.wechatUrl">已填链接</span>
          </div>
        </div>
        <div class="item-actions" @click.stop>
          <button type="button" class="btn btn-edit btn-sm" @click="openItemEdit(idx)">编辑</button>
          <button type="button" class="btn btn-delete btn-sm" @click="removeItem(idx)">删除</button>
        </div>
      </div>
    </div>

    <div v-if="itemModalVisible" class="modal-mask" @click.self="closeItemModal">
      <div class="modal-dialog" role="dialog" aria-modal="true" @click.stop>
        <div class="modal-head">
          <h4 class="modal-title">{{ itemModalMode === 'add' ? '新增条目' : '编辑条目' }}</h4>
          <button type="button" class="btn-modal-close" title="关闭" @click="closeItemModal">×</button>
        </div>
        <div class="modal-body">
          <label class="field">
            <span>标题</span>
            <input v-model="itemForm.title" placeholder="例如：2026 年度表彰名单" />
          </label>
          <label class="field">
            <span>日期</span>
            <input v-model="itemForm.date" type="date" />
          </label>
          <div class="field">
            <span>封面图（可选）</span>
            <div class="img-row">
              <div class="upload-box upload-box--sm" @click="pickItemCover" @contextmenu.prevent="pickItemCover">
                <img v-if="itemForm.coverUrl" class="upload-img" :src="toFullUrl(itemForm.coverUrl)" alt="" />
                <div v-else class="upload-placeholder">
                  <div class="upload-plus">+</div>
                  <div class="upload-text">点击上传封面</div>
                </div>
              </div>
              <div class="upload-hint">提示：点击图片可更改。</div>
            </div>
            <input ref="itemCoverInput" class="hidden-input" type="file" accept="image/*" @change="onItemCoverChange" />
          </div>

          <template v-if="activeTab === 'article'">
            <label class="field full">
              <span>公众号链接</span>
              <div class="url-row">
                <input v-model="itemForm.wechatUrl" type="url" class="input-url" placeholder="https://mp.weixin.qq.com/..." />
                <button type="button" class="btn btn-edit" :disabled="resolvingTitle" @click="resolveWechatTitle">
                  {{ resolvingTitle ? '识别中...' : '识别标题' }}
                </button>
              </div>
            </label>
          </template>

          <template v-else>
            <div class="field full">
              <span>PDF 文件</span>
              <div class="file-row">
                <button type="button" class="btn btn-edit" :disabled="uploadingFile" @click="pickPdfFile">
                  {{ uploadingFile ? '上传中...' : '上传 PDF' }}
                </button>
                <span class="file-val">{{ itemForm.fileUrl ? '已上传' : '未上传' }}</span>
              </div>
              <input ref="pdfInput" class="hidden-input" type="file" accept="application/pdf" @change="onPdfChange" />
              <small class="subtip">也可直接粘贴 PDF 链接到下方。</small>
              <input v-model="itemForm.fileUrl" class="input-url" placeholder="PDF 链接（可粘贴）" />
            </div>
          </template>
        </div>
        <div class="modal-foot">
          <button type="button" class="btn btn-edit" @click="closeItemModal">取消</button>
          <button type="button" class="btn btn-primary" @click="confirmItemModal">确定</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import FancySelect from '@/components/FancySelect.vue'
import {
  createPageContent,
  getPageContents,
  getTilePageTypes,
  getTiles,
  resolveArticleTitleByUrl,
  toMediaUrl,
  updatePageContent,
  updateTile,
  uploadFile,
  uploadImage,
} from '@/api'
import backAboutIcon from '@/assets/icons/back-about.svg'
import backAboutActiveIcon from '@/assets/icons/back-about-active.svg'

function safeJsonParse(s, fallback) {
  try {
    return JSON.parse(s)
  } catch {
    return fallback
  }
}

function genKey() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`
}

function emptyItem() {
  return {
    _k: genKey(),
    title: '',
    date: '',
    coverUrl: '',
    wechatUrl: '',
    fileUrl: ''
  }
}

export default {
  name: 'AwardManageView',
  components: { FancySelect },
  data() {
    return {
      backBtnHover: false,
      backAboutIcon,
      backAboutActiveIcon,
      errorMsg: '',
      okMsg: '',
      saving: false,
      statusOptions: [
        { value: 'ENABLED', label: '启用' },
        { value: 'DISABLED', label: '停用' }
      ],
      pageTypeOptions: [{ value: '', label: '（不标注类型）' }],
      tileId: null,
      tileRaw: null,
      tileForm: {
        title: '表彰公示',
        imageUrl: '',
        statusCode: 'ENABLED',
        pageTypeId: '',
        sortOrder: 0,
      },
      activeTab: 'doc',
      rowId: null,
      persistedContentKey: 'award',
      docItems: [],
      articleItems: [],
      draggingIndex: -1,
      itemModalVisible: false,
      itemModalMode: 'add',
      itemEditIndex: -1,
      itemForm: emptyItem(),
      resolvingTitle: false,
      uploadingFile: false,
    }
  },
  computed: {
    currentItems() {
      return this.activeTab === 'article' ? (this.articleItems || []) : (this.docItems || [])
    }
  },
  mounted() {
    this.loadPageTypeOptions()
    this.load()
  },
  methods: {
    goBackToTiles() {
      this.$router.push('/cms/home/tile')
    },
    toFullUrl(url) {
      const u = String(url || '').trim()
      if (!u) return ''
      if (u.startsWith('data:')) return u
      return toMediaUrl(u)
    },
    async loadPageTypeOptions() {
      try {
        const rows = (await getTilePageTypes({ status: 'ENABLED' })) || []
        this.pageTypeOptions = [
          { value: '', label: '（不标注类型）' },
          ...rows.map((x) => ({ value: String(x.id), label: String(x.pageName || '') })).filter((x) => x.label)
        ]
      } catch {
        this.pageTypeOptions = [{ value: '', label: '（不标注类型）' }]
      }
    },
    async loadAwardTile() {
      const rows = (await getTiles()) || []
      const queryTileId = Number(this.$route?.query?.tileId || 0)
      let tile = null
      if (queryTileId > 0) tile = rows.find((x) => Number(x.id) === queryTileId) || null
      if (!tile) {
        tile = rows.find((x) => {
          const title = String(x?.title || '').trim()
          const code = String(x?.linkTarget || '')
          return /[?&]code=award(?:&|$)/.test(code) || title === '表彰公示'
        }) || null
      }
      if (!tile) {
        this.errorMsg = '未找到“表彰公示”首页入口，请先在首页入口管理中创建。'
        return
      }
      this.tileId = Number(tile.id)
      this.tileRaw = { ...tile }
      this.tileForm = {
        title: String(tile.title || '表彰公示'),
        imageUrl: String(tile.imageUrl || ''),
        statusCode: String(tile.statusCode || 'ENABLED'),
        pageTypeId: tile.pageTypeId != null && tile.pageTypeId !== '' ? String(tile.pageTypeId) : '',
        sortOrder: Number(tile.sortOrder || 0),
      }
    },
    async load() {
      this.errorMsg = ''
      this.okMsg = ''
      try {
        await this.loadAwardTile()
        const list = (await getPageContents()) || []
        const row = list.find((it) => (it.contentKey === 'award' || it.contentKey === 'award_list') && (it.isDeleted === 0 || typeof it.isDeleted === 'undefined')) || null
        if (row) {
          this.rowId = row.id
          this.persistedContentKey = String(row.contentKey || 'award').trim() || 'award'
          const cfg = safeJsonParse(row.content || '', null)
          if (cfg && cfg.variant === 'award') {
            this.docItems = (Array.isArray(cfg.docs) ? cfg.docs : []).map((x) => ({ _k: genKey(), ...x }))
            this.articleItems = (Array.isArray(cfg.articles) ? cfg.articles : []).map((x) => ({ _k: genKey(), ...x }))
          }
        }
      } catch (e) {
        this.errorMsg = e.message || '读取失败'
      }
    },
    buildPayload() {
      const docs = (this.docItems || []).map((it) => ({
        title: String(it.title || '').trim(),
        date: String(it.date || '').trim(),
        coverUrl: String(it.coverUrl || '').trim(),
        fileUrl: String(it.fileUrl || '').trim(),
      })).filter((x) => x.title)
      const articles = (this.articleItems || []).map((it) => ({
        title: String(it.title || '').trim(),
        date: String(it.date || '').trim(),
        coverUrl: String(it.coverUrl || '').trim(),
        wechatUrl: String(it.wechatUrl || '').trim(),
      })).filter((x) => x.title && x.wechatUrl)
      return {
        contentKey: this.persistedContentKey || 'award',
        title: '表彰公示',
        content: JSON.stringify({ variant: 'award', docs, articles }),
        statusCode: 'ENABLED',
        remark: '表彰公示（文档/文章）配置（JSON）'
      }
    },
    async saveAll() {
      this.errorMsg = ''
      this.okMsg = ''
      this.saving = true
      try {
        if (!this.tileId) throw new Error('表彰公示入口不存在，无法保存。')
        if (!String(this.tileForm.title || '').trim()) throw new Error('请填写入口标题')
        if (!String(this.tileForm.imageUrl || '').trim()) throw new Error('请上传入口图片')
        const raw = this.tileRaw || {}
        await updateTile(this.tileId, {
          ...raw,
          pageTypeId: this.tileForm.pageTypeId ? Number(this.tileForm.pageTypeId) : null,
          linkTarget: String(raw.linkTarget || '/pages/article/list?code=award'),
          title: String(this.tileForm.title || '').trim(),
          imageUrl: String(this.tileForm.imageUrl || '').trim(),
          statusCode: String(this.tileForm.statusCode || 'ENABLED'),
          sortOrder: Number(this.tileForm.sortOrder || 0),
        })
        const payload = this.buildPayload()
        if (this.rowId) {
          await updatePageContent(this.rowId, payload)
        } else {
          const created = await createPageContent(payload)
          this.rowId = created?.id || this.rowId
        }
        this.okMsg = '保存成功'
      } catch (e) {
        this.errorMsg = e.message || '保存失败'
      } finally {
        this.saving = false
      }
    },
    pickTileImage() {
      this.$refs.tileImageInput?.click()
    },
    async onTileImageChange(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      try {
        const data = await uploadImage(file)
        const url = String(data?.url || '').trim()
        if (!url) throw new Error('未获取到图片URL')
        this.tileForm.imageUrl = url
      } catch (err) {
        this.errorMsg = err.message || '上传失败'
      } finally {
        e.target.value = ''
      }
    },
    openItemAdd() {
      this.itemModalMode = 'add'
      this.itemEditIndex = -1
      this.itemForm = emptyItem()
      this.itemModalVisible = true
    },
    openItemEdit(idx) {
      const it = this.currentItems[idx]
      if (!it) return
      this.itemModalMode = 'edit'
      this.itemEditIndex = idx
      this.itemForm = { ...emptyItem(), ...it }
      this.itemModalVisible = true
    },
    closeItemModal() {
      if (this.uploadingFile || this.resolvingTitle) return
      this.itemModalVisible = false
      this.itemEditIndex = -1
    },
    confirmItemModal() {
      const title = String(this.itemForm.title || '').trim()
      if (!title) {
        this.errorMsg = '请填写标题'
        return
      }
      if (this.activeTab === 'article') {
        const url = String(this.itemForm.wechatUrl || '').trim()
        if (!url) {
          this.errorMsg = '请填写公众号链接'
          return
        }
      } else {
        const url = String(this.itemForm.fileUrl || '').trim()
        if (!url) {
          this.errorMsg = '请上传或填写 PDF 链接'
          return
        }
      }
      const arr = this.activeTab === 'article' ? [...this.articleItems] : [...this.docItems]
      const payload = {
        _k: this.itemModalMode === 'add' ? genKey() : arr[this.itemEditIndex]._k,
        title,
        date: String(this.itemForm.date || '').trim(),
        coverUrl: String(this.itemForm.coverUrl || '').trim(),
        wechatUrl: String(this.itemForm.wechatUrl || '').trim(),
        fileUrl: String(this.itemForm.fileUrl || '').trim(),
      }
      if (this.itemModalMode === 'add') {
        arr.push(payload)
      } else {
        if (this.itemEditIndex < 0 || this.itemEditIndex >= arr.length) return
        arr[this.itemEditIndex] = { ...arr[this.itemEditIndex], ...payload }
      }
      if (this.activeTab === 'article') this.articleItems = arr
      else this.docItems = arr
      this.errorMsg = ''
      this.itemModalVisible = false
      this.itemEditIndex = -1
    },
    removeItem(idx) {
      const arr = this.activeTab === 'article' ? [...this.articleItems] : [...this.docItems]
      arr.splice(idx, 1)
      if (this.activeTab === 'article') this.articleItems = arr
      else this.docItems = arr
    },
    onDragStart(idx) {
      this.draggingIndex = idx
    },
    onDrop(dropIdx) {
      const from = this.draggingIndex
      this.draggingIndex = -1
      if (from < 0 || from === dropIdx) return
      const arr = this.activeTab === 'article' ? [...this.articleItems] : [...this.docItems]
      const moved = arr.splice(from, 1)[0]
      arr.splice(dropIdx, 0, moved)
      if (this.activeTab === 'article') this.articleItems = arr
      else this.docItems = arr
    },
    onDragEnd() {
      this.draggingIndex = -1
    },
    pickItemCover() {
      this.$refs.itemCoverInput?.click()
    },
    async onItemCoverChange(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      try {
        const data = await uploadImage(file)
        const url = String(data?.url || '').trim()
        if (!url) throw new Error('未获取到图片URL')
        this.itemForm = { ...this.itemForm, coverUrl: url }
      } catch (err) {
        this.errorMsg = err.message || '上传失败'
      } finally {
        e.target.value = ''
      }
    },
    async resolveWechatTitle() {
      const url = String(this.itemForm.wechatUrl || '').trim()
      if (!url) {
        this.errorMsg = '请先填写公众号链接'
        return
      }
      try {
        this.resolvingTitle = true
        const data = await resolveArticleTitleByUrl(url)
        const title = String(data?.title || '').trim()
        if (title) this.itemForm = { ...this.itemForm, title }
      } catch (e) {
        this.errorMsg = e.message || '识别标题失败'
      } finally {
        this.resolvingTitle = false
      }
    },
    pickPdfFile() {
      this.$refs.pdfInput?.click()
    },
    async onPdfChange(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      this.uploadingFile = true
      try {
        const data = await uploadFile(file)
        const url = String(data?.url || '').trim()
        if (!url) throw new Error('未获取到文件URL')
        this.itemForm = { ...this.itemForm, fileUrl: url }
      } catch (err) {
        this.errorMsg = err.message || '上传失败'
      } finally {
        this.uploadingFile = false
        e.target.value = ''
      }
    },
  }
}
</script>

<style scoped>
.card { background: #fff; border-radius: 12px; padding: 18px; }
.card * { box-sizing: border-box; }
.page-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 4px; }
.page-head h2 { margin: 0; }
.back-btn { width: 40px; height: 40px; border: none; background: transparent; cursor: pointer; margin-bottom: 8px; padding: 0; display: inline-flex; align-items: center; justify-content: center; transition: transform .15s ease; }
.back-btn:hover { transform: translateX(-1px); }
.back-btn-icon { width: 32px; height: 32px; display: block; }
.tip { background: #f5f8ff; border: 1px solid #dae5ff; color: #2a4b8d; padding: 10px 12px; border-radius: 8px; margin-bottom: 10px; line-height: 1.6; }
.error { color: #d92d20; margin: 8px 0; }
.ok { color: #067647; margin: 8px 0; }
.panel { border: 1px solid #eef2f7; border-radius: 12px; padding: 14px; background: #fff; }
.panel + .panel { margin-top: 12px; }
.panel-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 10px; }
.panel h3 { margin: 0; font-size: 14px; color: #101828; }
.tile-panel { margin-bottom: 12px; }
.tile-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.tile-upload { grid-column: span 2; }
.field { display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: #344054; min-width: 0; }
.field.inline { min-width: 220px; }
.field > span { font-weight: 700; }
.field input, .input-url { border: 1px solid #d0d7e2; border-radius: 10px; height: 40px; padding: 0 10px; font-size: 13px; width: 100%; min-width: 0; }
.subtip { color: #98a2b3; font-size: 12px; margin: 0 0 10px; }
.empty { color: #98a2b3; font-size: 13px; padding: 8px 0; }
.tabs { display: inline-flex; align-items: center; gap: 8px; margin: 10px 0 12px; padding: 4px; border-radius: 12px; background: #eef2f7; }
.tab { border: 1px solid transparent; background: transparent; color: #475467; border-radius: 10px; padding: 10px 18px; font-size: 14px; font-weight: 600; cursor: pointer; }
.tab:hover { background: rgba(255, 255, 255, 0.7); color: #26457d; }
.tab.active { background: #fff; color: #2f6af6; border-color: #d0d7e2; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08); }
.btn { border-radius: 10px; padding: 8px 12px; cursor: pointer; border: 1px solid transparent; font-size: 12px; }
.btn-primary { background: #2f6af6; color: #fff; }
.btn-edit { border-color: #bfc9db; background: #fff; color: #26457d; }
.btn-delete { border-color: #ffc8c2; background: #fff1f0; color: #d92d20; }
.btn-sm { padding: 6px 10px; }
.img-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.upload-box { width: 220px; height: 140px; border-radius: 12px; border: 1px dashed #cbd5e1; background: #f8fafc; display: flex; align-items: center; justify-content: center; overflow: hidden; cursor: pointer; transition: border-color .18s ease, background .18s ease, box-shadow .18s ease; }
.upload-box--sm { width: 180px; height: 110px; }
.upload-box:hover { border-color: #98b4ff; background: #f3f7ff; box-shadow: 0 8px 20px rgba(47, 106, 246, 0.12); }
.upload-img { width: 100%; height: 100%; object-fit: cover; }
.upload-placeholder { color: #98a2b3; font-size: 12px; padding: 8px 10px; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 6px; }
.upload-plus { width: 26px; height: 26px; border-radius: 999px; border: 1px solid #c8d7ff; background: #fff; color: #2f6af6; font-size: 18px; display: inline-flex; align-items: center; justify-content: center; }
.upload-text { color: #667085; font-size: 12px; }
.upload-hint { color: #98a2b3; font-size: 12px; }
.hidden-input { display: none; }
.item-row { display: flex; align-items: center; gap: 10px; border: 1px solid #eef2f7; border-radius: 10px; padding: 10px 12px; margin-top: 8px; background: #fbfdff; }
.drag-handle { color: #98a2b3; font-size: 14px; user-select: none; cursor: grab; }
.item-idx { width: 22px; height: 22px; border-radius: 8px; background: #eef4ff; color: #2f6af6; font-size: 11px; font-weight: 800; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.item-main { flex: 1; min-width: 0; }
.item-title { font-size: 13px; font-weight: 600; color: #101828; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.item-meta { margin-top: 4px; font-size: 12px; color: #98a2b3; display: flex; gap: 10px; }
.item-actions { display: flex; gap: 6px; flex-shrink: 0; }
.modal-mask { position: fixed; inset: 0; z-index: 1000; background: rgba(15, 23, 42, 0.45); display: flex; align-items: center; justify-content: center; padding: 16px; }
.modal-dialog { width: 100%; max-width: 560px; max-height: min(90vh, 760px); background: #fff; border-radius: 14px; box-shadow: 0 20px 50px rgba(15, 23, 42, 0.2); display: flex; flex-direction: column; overflow: hidden; }
.modal-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 16px; border-bottom: 1px solid #eef2f7; }
.modal-title { margin: 0; font-size: 16px; color: #101828; }
.btn-modal-close { border: none; background: transparent; font-size: 22px; line-height: 1; color: #98a2b3; cursor: pointer; padding: 4px 8px; border-radius: 8px; }
.btn-modal-close:hover { background: #f2f4f7; color: #344054; }
.modal-body { padding: 14px 16px; overflow-y: auto; flex: 1; }
.modal-foot { display: flex; justify-content: flex-end; gap: 10px; padding: 12px 16px; border-top: 1px solid #eef2f7; background: #fafbfc; }
.url-row { display: flex; gap: 8px; align-items: center; }
.url-row .input-url { flex: 1; }
.file-row { display: flex; align-items: center; gap: 10px; }
.file-val { font-size: 12px; color: #667085; }
@media (max-width: 1080px) { .tile-grid { grid-template-columns: 1fr; } .tile-upload { grid-column: span 1; } }
</style>

